import { useEffect, useState } from 'react';
import {
  onSnapshot,
  query,
  where,
  orderBy,
  type Query,
  type DocumentReference,
  type DocumentData,
  type QuerySnapshot,
} from 'firebase/firestore';

export { query, where, orderBy };

/**
 * Subscribes to a Firestore query and returns a typed array of documents
 * with their IDs merged in.
 *
 * Pass a `queryKey` string that changes whenever the query parameters change
 * (e.g. a cycle ID, user ID). The hook re-subscribes whenever `queryKey`
 * changes, so callers that derive queries from async state must supply one.
 * Callers whose query is fully stable at mount time can omit it.
 */
export function useCollection<T extends DocumentData>(
  q: Query<T> | null,
  queryKey?: string,
): { data: (T & { id: string })[]; loading: boolean; error: Error | null } {
  const [data, setData] = useState<(T & { id: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!q) {
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsub = onSnapshot(
      q,
      (snap: QuerySnapshot<T>) => {
        setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
    );

    return unsub;
    // queryKey (not q) drives resubscription, because q is a fresh object every
    // render. `!!q` must be in here too: a caller whose query is gated on state
    // that resolves LATER than its key (e.g. waiting for the school doc to know
    // if a module is on) would otherwise stabilise its key while q is still
    // null, and never subscribe once the query appeared.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey ?? '___stable___', !!q]);

  return { data, loading, error };
}

/**
 * Subscribes to a single Firestore document and returns the typed value
 * with its ID merged in, or null if it doesn't exist.
 */
export function useDocument<T extends DocumentData>(
  ref: DocumentReference<T> | null,
): { data: (T & { id: string }) | null; loading: boolean; error: Error | null } {
  const [data, setData] = useState<(T & { id: string }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  // Which document the current answer is for. In the render where `ref` first
  // appears (or changes), the effect below hasn't run yet, so `loading` still
  // says false — and a caller would read "no data, not loading" as "doesn't
  // exist" for one frame.
  const [answeredPath, setAnsweredPath] = useState<string | null>(null);

  useEffect(() => {
    if (!ref) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsub = onSnapshot(
      ref,
      (snap) => {
        setData(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        setLoading(false);
        setAnsweredPath(ref.path);
      },
      (err) => {
        setError(err);
        setLoading(false);
        setAnsweredPath(ref.path);
      },
    );

    return unsub;
    // Re-subscribe when the target document changes — e.g. ref goes from null
    // to a real doc once auth resolves. ref.path is stable for the same
    // document, so fresh ref object identities don't cause loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref?.path ?? '___null___']);

  return { data, loading: loading || (!!ref && answeredPath !== ref.path), error };
}
